import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { CheckCircle, Star, Group, NoteAlt } from '@mui/icons-material';
import { ROW_HEIGHT } from '../constants/layout';

// Color del canal/origen — stripe lateral en la barra
const CHANNEL_COLORS = {
  Direct: '#FFFFFF',
  Booking: '#FCD34D',
  'Booking.com': '#FCD34D',
  Expedia: '#FBBF24',
  Airbnb: '#FB7185',
  Corporate: '#F472B6',
  'Walk-in': '#93C5FD',
  Email: '#A7F3D0'
};

// Colores fuertes/débiles del estado para el gradiente vertical
const STATUS_GRADIENT = {
  Confirmada:    ['#22c55e', '#16A34A'],
  Pendiente:     ['#fbbf24', '#D97706'],
  Tentativa:     ['#fbbf24', '#D97706'],
  'Lista Espera':['#a78bfa', '#7C3AED'],
  'En Casa':     ['#3b82f6', '#2563EB'],
  'En Uso':      ['#3b82f6', '#2563EB'],
  Llegando:      ['#3b82f6', '#2563EB'],
  Saliendo:      ['#a78bfa', '#7C3AED'],
  'Check-out':   ['#a78bfa', '#7C3AED'],
  'No Show':     ['#475569', '#334155']
};

export default function ReservationBar({ reservation, viewMode, rangeStart, cellWidth, totalDays, onOpenReservation }) {
  const startDate = dayjs(reservation.start);
  const endDate = dayjs(reservation.end);
  const rangeStartDay = dayjs(rangeStart).startOf('day');

  let left, width;
  let openLeft = false;
  let openRight = false;

  if (viewMode === 'hourly') {
    const startHour = startDate.hour() + startDate.minute() / 60;
    const endsNextDay = endDate.isAfter(rangeStartDay, 'day');
    const endHourRaw = endDate.hour() + endDate.minute() / 60;
    const visibleEndHour = endsNextDay ? 24 : endHourRaw;

    if (startDate.isSame(rangeStartDay, 'day')) {
      left = startHour * cellWidth;
      width = (visibleEndHour - startHour) * cellWidth;
      if (endsNextDay) openRight = true;
    } else if (startDate.isBefore(rangeStartDay, 'day') && endDate.isAfter(rangeStartDay, 'day')) {
      left = 0;
      width = 24 * cellWidth;
      openLeft = openRight = true;
    } else if (startDate.isBefore(rangeStartDay, 'day') && endDate.isSame(rangeStartDay, 'day')) {
      left = 0;
      width = Math.max(0, endHourRaw * cellWidth);
      openLeft = true;
    } else {
      return null;
    }
  } else {
    const startDiffRaw = startDate.diff(rangeStartDay, 'day');
    const endDiffRaw = endDate.diff(rangeStartDay, 'day');

    const startDiff = Math.max(0, startDiffRaw);
    const endDiff = Math.min(totalDays, endDiffRaw);
    left = startDiff * cellWidth;
    width = (endDiff - startDiff) * cellWidth;
    if (startDiffRaw < 0) openLeft = true;
    if (endDiffRaw > totalDays) openRight = true;
  }

  if (width <= 0) return null;

  const showFullName = width > 100;
  const showAnyText = width > 30;
  const isHourlyBooking = reservation.nights < 1 || endDate.diff(startDate, 'hour') < 24;

  // Cancelada/no-show: fondo gris y texto tachado
  const isCancelled = reservation.status === 'cancelled' || reservation.statusLabel === 'Cancelada';
  const isNoShow = reservation.status === 'no_show' || reservation.statusLabel === 'No Show';

  // Gradiente por estado
  const gradient = STATUS_GRADIENT[reservation.statusLabel];
  const background = isCancelled
    ? '#CBD5E1'
    : isNoShow
    ? 'linear-gradient(180deg, #475569, #334155)'
    : gradient
    ? `linear-gradient(180deg, ${gradient[0]}, ${gradient[1]})`
    : reservation.color;

  // Bisel: media celda (acotado para barras muy cortas)
  const HALF = Math.min(14, Math.max(0, width / 4));
  const bevelL = openLeft ? 0 : HALF;
  const bevelR = openRight ? 0 : HALF;
  const clipPath = `polygon(${bevelL}px 0, calc(100% - ${bevelR}px) 0, 100% 50%, calc(100% - ${bevelR}px) 100%, ${bevelL}px 100%, 0 50%)`;

  // Canal/origen
  const sourceColor = CHANNEL_COLORS[reservation.source] || '#E5E9F2';

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5, minWidth: 220 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              {reservation.title}
              {reservation.vip && <Star sx={{ fontSize: 12, ml: 0.5, color: '#FCD34D', verticalAlign: '-2px' }} />}
            </Typography>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                fontSize: '0.6rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                background: gradient ? gradient[1] : reservation.color,
                color: '#fff'
              }}
            >
              {reservation.statusLabel}
            </Box>
          </Stack>
          <Stack spacing={0.4}>
            {isHourlyBooking ? (
              <>
                <Typography variant="caption">
                  ⏰ {startDate.format('HH:mm')} – {endDate.format('HH:mm')}
                </Typography>
                <Typography variant="caption">
                  ⏱️ {endDate.diff(startDate, 'hour', true).toFixed(1)} h
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="caption">
                  📅 {startDate.format('DD/MM/YYYY')} → {endDate.format('DD/MM/YYYY')}
                </Typography>
                <Typography variant="caption">
                  🌙 {reservation.nights} {reservation.nights === 1 ? 'noche' : 'noches'}
                </Typography>
              </>
            )}
            <Typography variant="caption">
              👥 {reservation.adults} adultos {reservation.children > 0 && `+ ${reservation.children} niños`}
            </Typography>
            {reservation.price && <Typography variant="caption">💰 ${reservation.price}</Typography>}
            {reservation.confirmationNumber && (
              <Typography variant="caption">🔖 {reservation.confirmationNumber}</Typography>
            )}
            {reservation.source && <Typography variant="caption">🌐 {reservation.source}</Typography>}
            {reservation.groupCode && <Typography variant="caption">👥 Grupo: {reservation.groupCode}</Typography>}
            {reservation.notes && <Typography variant="caption">📝 {reservation.notes}</Typography>}
          </Stack>
        </Box>
      }
    >
      <Box
        onClick={() => onOpenReservation?.(reservation.id)}
        sx={{
          position: 'absolute',
          left: `${left}px`,
          top: '6px',
          height: ROW_HEIGHT - 12,
          width: `${Math.max(2, width - 2)}px`,
          background,
          color: isCancelled ? '#334155' : '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          pl: '10px',
          pr: '8px',
          cursor: 'pointer',
          overflow: 'hidden',
          zIndex: 10,
          clipPath,
          boxShadow: '0 1px 0 rgba(0,0,0,.06), 0 1px 2px rgba(15,23,42,.12)',
          transition: 'transform .12s ease, box-shadow .12s ease, filter .12s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            filter: 'brightness(1.06)',
            boxShadow: '0 4px 12px rgba(15,23,42,.18)',
            zIndex: 100
          }
        }}
      >
        {/* Stripe del canal/origen */}
        {!openLeft && (
          <Box
            sx={{
              position: 'absolute',
              left: bevelL + 2,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: 1.5,
              backgroundColor: sourceColor,
              opacity: 0.85
            }}
          />
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ width: '100%', minWidth: 0, ml: openLeft ? 0 : '6px' }}
        >
          {reservation.checkedIn && !reservation.checkedOut && <CheckCircle sx={{ fontSize: 13 }} />}
          {reservation.vip && <Star sx={{ fontSize: 13, color: '#FCD34D' }} />}
          {reservation.groupCode && <Group sx={{ fontSize: 13 }} />}
          {reservation.notes && width > 60 && <NoteAlt sx={{ fontSize: 12, opacity: 0.85 }} />}
          {showAnyText && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                textDecoration: isCancelled ? 'line-through' : 'none'
              }}
            >
              {showFullName ? reservation.title : reservation.title.split(' ')[0]}
            </Typography>
          )}
          {reservation.price && width > 90 && (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.65rem', opacity: 0.95, flexShrink: 0, fontWeight: 700 }}
            >
              ${reservation.price}
            </Typography>
          )}
        </Stack>
      </Box>
    </Tooltip>
  );
}
